/**
 * Sanitize a possibly-incomplete markdown string for display during the
 * IN-PROGRESS (still streaming) state of an assistant message.
 *
 * While the model types a message character-by-character, the raw markdown
 * syntax of a token that has not finished generating is briefly visible — you
 * literally see `[label](http...` before it snaps into a rendered link. This
 * helper hides that raw syntax for the trailing, still-incomplete token so the
 * stream only ever reveals plain label text (or nothing) until the token is
 * fully formed, at which point the untouched markdown renders normally.
 *
 * It deliberately only neutralizes the trailing/incomplete region. Any
 * fully-formed markdown earlier in the string passes through unchanged, so the
 * caller MUST stop applying this once the message is finalized and render the
 * complete markdown verbatim.
 *
 * Scope is intentionally narrow (it is NOT a markdown parser): bracket runs the
 * settle will fold ({@link holdBackBracketRuns}), incomplete inline links and
 * images, plus a dangling inline-code or emphasis marker at the very tail.
 */
export function sanitizeStreamingMarkdown(partial: string): string {
  if (!partial) return partial;

  let out = partial;
  out = holdBackBracketRuns(out);
  out = stripIncompleteLink(out);
  out = neutralizeTrailingInlineCode(out);
  out = neutralizeTrailingEmphasis(out);
  return out;
}

/**
 * Hold back the bracket runs the settle is going to delete or renumber.
 *
 * A bracket run only becomes a LINK once `](` follows it. Until then it is a
 * note, and warhol's settle (`place_markers`) folds notes
 * (`[Regulation, Annex, p. 16; Claims Powerhouse, A534:C534]`) into `[n]` chips
 * and renumbers every citation marker against a harvest that does not exist
 * until the turn ends. Rendered while the row streams, the reader is shown the
 * note text and a `[1]` that can land as `[7]` — or as nothing — and then
 * watches it change or vanish at settle. Holding the run back means the chip
 * appears once, already numbered.
 *
 * Deliberately the same rule christo's stopped-answer stripper applies
 * (`services/christo/lib/services/askPunttChat.ts::stripBracketRuns`): the
 * stream and the settle must agree on what is a note, or stopping a turn
 * rewrites the paragraph the person was reading. That agreement covers the
 * escape guard too — christo's `BRACKET_RUN` carries `(?<!\\)`, so every
 * bracket rule here ({@link stripIncompleteLink} included) must skip an
 * escaped `[` or the stream shows text the settle will not.
 *
 * Two shapes, both scoped to a single line:
 *   - `[note]` / `![note]` not followed by `(` — a complete note or marker.
 *   - `[half a not` at the end of a line — the same run still arriving.
 * Each carries the `[ \t]*` that led up to it, exactly as christo's
 * `BRACKET_RUN` does. Without that prefix `off-brand [note]` streams as
 * `off-brand ` and settles as `off-brand`, so the trailing space the reader
 * sees disappears at settle — the same one-character jump the hold-back
 * exists to prevent. The escape guard sits AFTER that prefix, so it still
 * reads the character immediately before the `[`: in `a\ [note]` the
 * backslash escapes the SPACE, not the bracket, and the note is held back.
 *
 * The class is `[^\]\n]`, NOT the `[^[\]\n]` {@link stripIncompleteLink} uses:
 * christo's `BRACKET_RUN` lets a run swallow an inner `[`, so `[Annex [sic]`
 * is ONE note there. Excluding `[` here would hold back only the inner pair
 * and stream `[Annex` — words the settle then deletes. The narrow class
 * belongs to `stripIncompleteLink`, which must keep one pair per match to
 * recover a label; a hold-back has no label to recover.
 *
 * An ESCAPED `\[` is the person's own literal bracket, not a note, so a run
 * whose `[` is backslash-escaped is skipped. The guard is christo's inline
 * `(?<!\\)` rather than a post-hoc test on the finished match, because the
 * wide class above lets ONE match swallow an inner `[`: a post-hoc check
 * reads only the FIRST `[`, so `\[a[b]` would be kept whole here while
 * {@link stripIncompleteLink}'s per-match lookbehind still rewrote the inner
 * `[b]` — streaming `\[ab`, which neither this file nor christo settles to.
 * Carried inline the pattern is byte-identical to christo's `BRACKET_RUN`,
 * so one escape rule covers the stream, the settle, and the step below.
 */
function holdBackBracketRuns(s: string): string {
  return s.replace(
    // why: the `(?<![ \t])` guard on each alternative is load-bearing: without it
    // the greedy `[ \t]*` is retried at every position INSIDE a whitespace run,
    // so the scan is O(run^2) — measured on node 20, a 16,001-space answer takes
    // 10.6 s (0 ms with the guard). This runs on every render while a row
    // streams, so one long run freezes the tab. Keeps the pattern byte-identical
    // to christo's `BRACKET_RUN`, as the comment above requires.
    /(?<![ \t])[ \t]*!?(?<!\\)\[[^\]\n]*\](?!\()|(?<![ \t])[ \t]*!?(?<!\\)\[[^\]\n]*(?=\n|$)/g,
    "",
  );
}

/**
 * Replace a trailing, not-yet-closed inline link or image with just its label
 * text. Anchored at the end of the string so earlier complete links are never
 * touched. Only `[label](partialUrl` reaches here: `holdBackBracketRuns` has
 * already deleted every unescaped `[label]` and `[partial` that is not
 * followed by `(`, so a second branch for those shapes cannot match.
 * A leading `!` (image) is consumed as well so an in-progress image shows its
 * alt text rather than a stray `!`.
 *
 * The `[^[\]\n]` character class keeps the match within a single bracket pair
 * on a single line, so a completed link like `[a](b)` (which ends in `)`)
 * cannot match.
 *
 * The `(?<!\\)` escape guard is the same one {@link holdBackBracketRuns}
 * applies: an escaped `\[` is the person's own literal bracket, not link
 * syntax, so it is left exactly as written.
 */
function stripIncompleteLink(s: string): string {
  // `[label](partialUrl` — open paren, no closing paren yet.
  const openUrl = /!?(?<!\\)\[([^[\]\n]*)\]\([^()\n]*$/;
  if (openUrl.test(s)) return s.replace(openUrl, "$1");

  return s;
}

/**
 * Remove a single unmatched inline-code backtick so a half-typed `` `code ``
 * span shows the code text plainly instead of a stray leading backtick. Fenced
 * code blocks (runs of backticks) are left alone — we only strip a lone
 * backtick that is not adjacent to another backtick.
 */
function neutralizeTrailingInlineCode(s: string): string {
  const backticks = (s.match(/`/g) ?? []).length;
  if (backticks % 2 === 0) return s;

  const idx = s.lastIndexOf("`");
  if (s[idx - 1] === "`" || s[idx + 1] === "`") return s;
  return s.slice(0, idx) + s.slice(idx + 1);
}

/**
 * Strip a dangling emphasis marker (`**`, `__`, `*`, `_`) when its count is odd
 * (an opener with no matching closer), so a half-typed `**bold` shows `bold`
 * rather than leading asterisks. Double markers are handled before single ones
 * so the two `*` of a `**` pair are not mistaken for unmatched single markers.
 */
function neutralizeTrailingEmphasis(s: string): string {
  let out = s;
  for (const marker of ["**", "__", "*", "_"]) {
    const count = out.split(marker).length - 1;
    if (count % 2 === 1) {
      const idx = out.lastIndexOf(marker);
      out = out.slice(0, idx) + out.slice(idx + marker.length);
    }
  }
  return out;
}
